import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getTemplate, PACKS, PACK_PRICE } from '@/data/templates';
import FormBuilder from '@/components/FormBuilder';
import DocumentPreview from '@/components/DocumentPreview';
import ReferralModal from '@/components/ReferralModal';
import LeadGateModal, { getSavedLeadEmail } from '@/components/LeadGateModal';
import { downloadPdf } from '@/lib/pdfGen';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFERRAL_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

function buildDefaults(template) {
  const v = {};
  for (const f of template.fields) {
    if (f.default !== undefined) v[f.key] = f.default;
    else if (f.type === 'number') v[f.key] = '';
    else if (f.type === 'lineitems') v[f.key] = [];
    else v[f.key] = '';
  }
  return v;
}

export default function Editor() {
  const { templateId } = useParams();
  const template = getTemplate(templateId);
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const isPro = !!user?.is_pro;
  const ownsPack = !!(template?.pack && (user?.owned_packs || []).includes(template.pack));
  const unlocked = isPro || ownsPack;
  const packMeta = template?.pack ? PACKS.find(p => p.slug === template.pack) : null;
  const [values, setValues] = useState(() => template ? buildDefaults(template) : {});
  const [refOpen, setRefOpen] = useState(false);
  const [leadGateOpen, setLeadGateOpen] = useState(false);
  const [buying, setBuying] = useState(false);

  const buyPack = async () => {
    if (!template?.pack) return;
    if (!user) { login(window.location.pathname); return; }
    setBuying(true);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_id: template.pack,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error('Could not start checkout. Please try again.');
      setBuying(false);
    }
  };

  useEffect(() => {
    if (template) setValues(buildDefaults(template));
  }, [templateId]); // eslint-disable-line

  const missing = useMemo(() => {
    if (!template) return [];
    return template.fields.filter(f => f.required && !values[f.key]);
  }, [template, values]);

  if (!template) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-3xl font-bold tracking-tight">Template not found</h1>
        <button onClick={() => navigate('/')} className="mt-6 underline text-orange-600">Back to home</button>
      </div>
    );
  }

  const performDownload = () => {
    try {
      downloadPdf(template, values, `${template.id}.pdf`, { watermark: !unlocked });
      if (!unlocked) {
        toast.success('PDF downloaded (with watermark)', {
          description: packMeta ? `Unlock the ${packMeta.name} for $${PACK_PRICE} to remove it.` : 'Upgrade to Pro to remove the watermark.',
          action: { label: packMeta ? 'Unlock' : 'Upgrade', onClick: () => (packMeta ? buyPack() : navigate('/pricing')) },
        });
        // Show the referral wall if not shown in the last 7 days and user hasn't claimed
        try {
          const lastShown = Number(localStorage.getItem('ref_modal_last') || 0);
          const alreadyClaimed = !!user?.referral_used;
          if (!alreadyClaimed && Date.now() - lastShown > REFERRAL_COOLDOWN_MS) {
            setTimeout(() => {
              setRefOpen(true);
              localStorage.setItem('ref_modal_last', String(Date.now()));
            }, 800);
          }
        } catch (e) {}
      } else {
        toast.success('PDF downloaded');
      }
      axios.post(`${API}/track/download`, {
        template_id: template.id,
        template_title: template.title,
      }).catch(() => {});
    } catch (e) {
      toast.error('Could not generate PDF');
    }
  };

  const onDownload = async () => {
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.map(m => m.label).join(', ')}`);
      return;
    }
    // Free-tier email gate: capture the lead first (once per browser).
    if (!unlocked && !getSavedLeadEmail()) {
      setLeadGateOpen(true);
      return;
    }
    performDownload();
  };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
      <Link to="/" className="text-sm text-stone-500 hover:text-orange-600 flex items-center gap-1 mb-4" data-testid="editor-back">
        <Icons.ArrowLeft size={14} /> Back
      </Link>

      <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">{template.category}</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{template.title}</h1>
          <p className="text-stone-500 mt-1 text-sm max-w-2xl">{template.description}</p>
        </div>
        <button
          onClick={onDownload}
          data-testid="download-pdf-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium btn-clay"
        >
          <Icons.Download size={16} /> {unlocked ? 'Download PDF' : 'Download (with watermark)'}
        </button>
      </div>

      {!unlocked && packMeta && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between bg-stone-900 text-white rounded-2xl px-5 py-4" data-testid="editor-unlock-banner">
          <div className="flex items-start gap-3">
            <Icons.Lock size={18} className="text-orange-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-semibold tracking-tight text-sm">Part of the {packMeta.name}</div>
              <div className="text-stone-300 text-sm">Unlock watermark-free downloads for every document in this pack — a one-time ${PACK_PRICE}.</div>
            </div>
          </div>
          <button
            onClick={buyPack}
            disabled={buying}
            data-testid="editor-buy-pack-btn"
            className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-5 py-2.5 text-sm font-medium btn-clay disabled:opacity-60"
          >
            {buying ? <Icons.Loader2 size={14} className="animate-spin" /> : <Icons.ShoppingBag size={14} />}
            Unlock for ${PACK_PRICE}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form (35%) */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="bg-white border border-stone-200 rounded-3xl p-6 lg:sticky lg:top-24" data-testid="editor-form">
            <FormBuilder template={template} values={values} setValues={setValues} />
          </div>
        </div>

        {/* Preview (65%) */}
        <div className="lg:col-span-7 xl:col-span-8">
          <div className="bg-stone-100 rounded-3xl p-4 sm:p-8 border border-stone-200" data-testid="editor-preview">
            <DocumentPreview template={template} values={values} />
          </div>
        </div>
      </div>

      <ReferralModal open={refOpen} onClose={() => setRefOpen(false)} />
      <LeadGateModal
        open={leadGateOpen}
        onClose={() => setLeadGateOpen(false)}
        onCaptured={() => {
          setLeadGateOpen(false);
          performDownload();
        }}
        templateTitle={template.title}
        templateId={template.id}
      />
    </div>
  );
}
